/**
 * Dynamic Tab Renderer Component
 *
 * Main tab navigation and content renderer.
 * Fetches page configuration, renders tabs, and displays active tab content.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useApplicationPageConfig } from '../../services/queries/configurationQueries';
import DynamicSectionRenderer from './DynamicSectionRenderer';
import * as LucideIcons from 'lucide-react';
import { RecordType } from '../../types/activity';
import type { TabSection } from '../../types/tabConfiguration';

interface DynamicTabRendererProps {
  /** Application identifier (e.g., 'cybersweep', 'credit-repair') */
  applicationName: string;
  /** Page configuration ID */
  pageId: string;
  /** Optional record ID for data fetching */
  recordId?: string;
  /** Record data for form sections (avoids mock data when provided) */
  recordData?: Record<string, unknown>;
  /** Current user role for visibility filtering */
  userRole: string;
  /** Optional custom class name */
  className?: string;
  /** Callback when View Audit Details action is triggered */
  onViewAuditDetails?: (auditId: string) => void;
  /** Contact name for activity modals */
  contactName?: string;
  /** Phone number for call modal */
  phoneNumber?: string;
  /** Related record name for meeting modal */
  relatedToName?: string;
  /** Record type for activity tab */
  recordType?: RecordType;
  /** Custom tab content renderers — maps tab ID to a render function that overrides default section rendering */
  customTabContent?: Record<string, () => React.ReactNode>;
  /** Explicit tab ids to hide from navigation */
  hiddenTabIds?: string[];
  /** Callback when the active tab changes — lets parent gate queries by active tab */
  onTabChange?: (tabId: string) => void;
  /** Imperative tab switch from parent (header actions). Change `nonce` to repeat same tab. */
  tabNavigationRequest?: { tabId: string; nonce: number } | null;
  /** Hide section description text under section titles */
  hideSectionDescription?: boolean;
  /** Optional per-tab section transformer (can inject/reorder/hide sections). */
  sectionTransformByTabId?: Partial<Record<string, (sections: TabSection[]) => TabSection[]>>;
  /** Callback when a field is saved. Optional return value for parent use; the form keeps the edited value on success. */
  onFieldSave?: (fieldName: string, value: unknown) => void | Promise<unknown>;
}

/**
 * Get Lucide icon component by name
 */
const getIconComponent = (iconName?: string) => {
  if (!iconName) return null;

  // Convert icon name to PascalCase for Lucide
  const iconKey = iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('') as keyof typeof LucideIcons;

  const IconComponent = LucideIcons[iconKey] as React.ComponentType<{ className?: string }>;
  return IconComponent || null;
};

/**
 * Dynamic Tab Renderer
 *
 * Fetches page configuration and renders tabs with content sections
 */
const DynamicTabRenderer: React.FC<DynamicTabRendererProps> = ({
  applicationName,
  pageId,
  recordId,
  recordData,
  userRole,
  className = '',
  onViewAuditDetails,
  contactName,
  phoneNumber,
  relatedToName,
  recordType = 'client',
  customTabContent,
  hiddenTabIds,
  onTabChange,
  tabNavigationRequest,
  hideSectionDescription = false,
  sectionTransformByTabId,
  onFieldSave,
}) => {
  // Fetch page configuration
  const { data: pageConfig, isLoading, error } = useApplicationPageConfig(pageId);

  // Active tab state
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  // Filter tabs based on user role visibility
  const visibleTabs = useMemo(() => {
    const tabs = pageConfig?.tabs;
    if (!tabs?.length) return [];
    const hidden = new Set(hiddenTabIds ?? []);
    return tabs.filter((tab) => {
      if (hidden.has(tab.id)) return false;
      if (!tab.visibility || !tab.visibility.roles || tab.visibility.roles.length === 0) {
        return true;
      }
      return tab.visibility.roles.includes(userRole);
    });
  }, [pageConfig?.tabs, userRole, hiddenTabIds]);

  // Set default active tab (first visible tab)
  useEffect(() => {
    if (visibleTabs.length > 0 && !activeTabId) {
      const defaultId = visibleTabs[0].id;
      setActiveTabId(defaultId);
      onTabChange?.(defaultId);
    }
  }, [visibleTabs, activeTabId, onTabChange]);

  // Primitives for exhaustive-deps (some eslint setups don't treat optional object props as stable deps).
  const tabNavigationTabId = tabNavigationRequest?.tabId;
  const tabNavigationNonce = tabNavigationRequest?.nonce;

  useEffect(() => {
    if (
      tabNavigationTabId == null ||
      tabNavigationTabId === '' ||
      visibleTabs.length === 0
    ) {
      return;
    }
    const target = visibleTabs.find((t) => t.id === tabNavigationTabId);
    if (target) {
      setActiveTabId(target.id);
      onTabChange?.(target.id);
    }
  }, [tabNavigationTabId, tabNavigationNonce, visibleTabs, onTabChange]);

  // Get active tab
  const activeTab = visibleTabs.find(tab => tab.id === activeTabId);
  const renderedSections = useMemo(() => {
    if (!activeTab) return [] as TabSection[];
    const baseSections = activeTab.sections ?? [];
    const transform = sectionTransformByTabId?.[activeTab.id];
    if (!transform) return baseSections;
    try {
      const transformed = transform(baseSections);
      return Array.isArray(transformed) ? transformed : baseSections;
    } catch {
      return baseSections;
    }
  }, [activeTab, sectionTransformByTabId]);

  /**
   * Handle tab click
   */
  const handleTabClick = (tabId: string) => {
    onTabChange?.(tabId);
    setActiveTabId(tabId);
  };

  /**
   * Loading state
   */
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading page configuration...</p>
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${className}`}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
          <h2 className="text-red-800 dark:text-red-200 font-semibold mb-2">
            Error Loading Configuration
          </h2>
          <p className="text-red-600 dark:text-red-400">
            {error instanceof Error ? error.message : 'Failed to load page configuration'}
          </p>
        </div>
      </div>
    );
  }

  /**
   * No configuration state
   */
  if (!pageConfig) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${className}`}>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 max-w-md">
          <h2 className="text-yellow-800 dark:text-yellow-200 font-semibold mb-2">
            No Configuration Found
          </h2>
          <p className="text-yellow-600 dark:text-yellow-400">
            Page configuration for "{pageId}" could not be found.
          </p>
        </div>
      </div>
    );
  }

  /**
   * No visible tabs state
   */
  if (visibleTabs.length === 0) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${className}`}>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 max-w-md">
          <h2 className="text-yellow-800 dark:text-yellow-200 font-semibold mb-2">
            No Tabs Available
          </h2>
          <p className="text-yellow-600 dark:text-yellow-400">
            You do not have permission to view any tabs on this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow ${className}`}>
      {/* Page Header */}
      {pageConfig.title && (
        <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {pageConfig.title}
          </h1>
          {pageConfig.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {pageConfig.description}
            </p>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-1 px-6" aria-label="Tabs">
          {visibleTabs.map(tab => {
            const isActive = tab.id === activeTabId;
            const IconComponent = getIconComponent(tab.icon);

            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors
                  ${isActive
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                {IconComponent && (
                  <IconComponent className="h-4 w-4 mr-2" />
                )}
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab && (
        <div className="p-6">
          {customTabContent?.[activeTab.id] ? (
            // Custom tab content override (e.g., Credit Block bureaus, cases, files)
            customTabContent[activeTab.id]()
          ) : renderedSections.length === 0 ? (
            // No sections in tab
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                No sections configured for this tab.
              </p>
            </div>
          ) : (
            // Render all sections in active tab
            <div className="space-y-6">
              {renderedSections.map(section => (
                <DynamicSectionRenderer
                  key={section.id}
                  section={section}
                  recordId={recordId}
                  recordData={recordData}
                  onViewAuditDetails={onViewAuditDetails}
                  contactName={contactName}
                  phoneNumber={phoneNumber}
                  relatedToName={relatedToName}
                  recordType={recordType}
                  hideSectionDescription={hideSectionDescription}
                  onFieldSave={onFieldSave}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DynamicTabRenderer;
